import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  // Legend, // Removed as it's not present in the original design and can be distracting for single series
  // ResponsiveContainer, // REMOVED for horizontal scrolling
} from 'recharts';
import { format, getDaysInMonth } from 'date-fns';

const StaffAttendanceChart = ({ data = [], year, month }) => {
  // Generate dummy data for the entire month if no actual data is provided
  const chartData = useMemo(() => {
    if (data && data.length > 0) {
      return data;
    }

    const daysInMonth = getDaysInMonth(new Date(year, month - 1));
    const dummyData = [];
    for (let i = 1; i <= daysInMonth; i++) {
      dummyData.push({
        day: String(i).padStart(2, '0'), // Format day as "01", "02", etc.
        hoursWorked: 0, // Default to 0 hours
      });
    }
    return dummyData;
  }, [data, year, month]);

  const yAxisConfig = useMemo(() => {
    // If there's no actual data provided (or all hours are zero), use a fixed smaller domain
    if (!data || !Array.isArray(data) || data.length === 0 || data.every(item => parseFloat(item.hoursWorked) === 0)) {
        return { domain: [0, 8], ticks: [0, 2, 4, 6, 8] };
    }

    const maxHours = data.reduce((max, item) => {
      const hours = parseFloat(item.hoursWorked);
      return Math.max(max, isNaN(hours) ? 0 : hours);
    }, 0);

    let yDomainEnd;
    if (maxHours <= 10) {
      yDomainEnd = 12;
    } else {
      yDomainEnd = Math.ceil(maxHours);
      if (yDomainEnd % 2 !== 0) {
        yDomainEnd += 1;
      } else {
        yDomainEnd += 2;
      }
    }

    const newTicks = [];
    for (let i = 0; i <= yDomainEnd; i += 2) {
      newTicks.push(i);
    }
    return { domain: [0, yDomainEnd], ticks: newTicks };
  }, [data]);

  // Calculate dynamic width for the chart based on the number of days and desired bar width
  const daysInMonth = chartData.length;
  const desiredBarWidth = 45; // Increased from 30
  const desiredBarGap = 10; // Gap between bars
  const chartPadding = 60; // Left and right padding for the chart area

  // Minimum width to ensure bars are visible, adjust as needed
  const calculatedChartWidth = (daysInMonth * (desiredBarWidth + desiredBarGap)) + chartPadding;
  // Ensure a minimum width so it doesn't shrink too much on small data sets
  const minChartWidth = 600; // Example minimum width
  const finalChartWidth = Math.max(calculatedChartWidth, minChartWidth);


  return (
    <div style={{ backgroundColor: '#1A1A1A', padding: '20px', borderRadius: '8px', color: '#FFF' }}>
      <h3 style={{ textAlign: 'center', marginBottom: '20px', fontFamily: 'Arial, sans-serif', color: '#FFF' }}>
        DAILY ATTENDANCE
      </h3>
      {/* Outer div for horizontal scrolling */}
      <div style={{ overflowX: 'auto', paddingBottom: '10px' }}> {/* Add padding-bottom for scrollbar visibility */}
        <BarChart
          width={finalChartWidth} // Set the dynamic width
          height={300}
          data={chartData}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#444" />
          <XAxis
            dataKey="day"
            tick={{ fill: '#FFF' }}
            axisLine={{ stroke: '#FFF' }}
          />
          <YAxis
            label={{ value: 'Hours Worked', angle: -90, position: 'insideLeft', fill: '#FFD700', dx: -10 }}
            tick={{ fill: '#FFF' }}
            axisLine={{ stroke: '#FFF' }}
            domain={yAxisConfig.domain}
            ticks={yAxisConfig.ticks}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#333', border: '1px solid #555', color: '#FFF' }}
            labelStyle={{ color: '#FFD700' }}
          />
          <Bar
            dataKey="hoursWorked"
            fill="#FFD700"
            name="Hours Worked"
            barSize={desiredBarWidth} // Increased bar width
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
      </div>
    </div>
  );
};

export default StaffAttendanceChart;